import { FileAccessScope } from '@unifyapps/defs/types/fileAccessScope';
import { HTTP_HEADERS } from '@unifyapps/network/auth/const';
import { executeWorkflowNode } from '@unifyapps/network/generated/workflow-rest-api/workflow-rest-api';
import {
  publicApiURL,
  publicPageURL,
} from '@unifyapps/network/utils/fetch/hooks/beforeRequest/interface';
import type { QueryMeta } from '@unifyapps/network/utils/fetch/types';
import getDomain from '@unifyapps/network/utils/url/getDomain';
import { isPrivateURL } from '@unifyapps/network/utils/url/isPrivateURL';
import { buildUrl } from '../../utils/url/buildUrl';
import type { UploadedFile } from './types';

// ALL_USERS is the only scope the original node can express — it predates accessScope and
// resolves to "any authenticated user". Everything else carries a permission it has no way
// to state, so those go to the asset node, which persists the scope alongside the file.
//
// PUBLIC is in this set even though a public file normally never gets here: on a bucket
// that accepts public objects the ACL is flipped instead and the caller returns the plain
// https URL above. Reaching this point with PUBLIC means the bucket refused, so the only
// way left to serve the file to unauthenticated callers is the asset node.
//
// This file is the fallback path — uppy-companion's utils/getPrivateUrl.cjs does the same
// resolution server-side and duplicates this set. Change both together.
const ASSET_NODE_SCOPES = new Set<FileAccessScope>([
  FileAccessScope.USER,
  FileAccessScope.APPLICATION,
  FileAccessScope.PUBLIC,
]);

const getAssetNodeRequest = (file: UploadedFile, source: string, bucket: string) => ({
  id: 'create_unifyapps_asset_from_file',
  context: {
    appName: 'utility_by_unifyapps',
    resourceName: 'utility_by_unifyapps_create_unifyapps_asset_from_file',
  },
  inputs: {
    file: { sourceType: 'CLOUD_STORAGE', source, bucket },
    accessScope: file.meta.accessScope,
    // only the builder sets this, so most uploads leave it undefined — and an undefined
    // disappears from the request body entirely rather than arriving as false
    isBuilderAsset: file.meta.isBuilderAsset ?? false,
  },
});

const getPathNodeRequest = (source: string, bucket: string) => ({
  id: 'create_unifyapps_path_for_file',
  context: {
    appName: 'utility_by_unifyapps',
    resourceName: 'utility_by_unifyapps_create_unifyapps_path_for_file',
  },
  inputs: {
    file: { sourceType: 'CLOUD_STORAGE', source, bucket },
  },
});

// APPLICATION-scoped files are readable per app, so the backend has to know which app
// this upload belongs to. Same resolution order as getUppyInstance's request headers.
const withAppIdHeader = (queryMeta?: QueryMeta): QueryMeta => ({
  ...queryMeta,
  headers: {
    ...(queryMeta?.headers as Record<string, string> | undefined),
    [HTTP_HEADERS.AppId]:
      queryMeta?.interfaceId ??
      (typeof window === 'undefined'
        ? ''
        : ((window as { __unifyAppId?: string }).__unifyAppId ?? '')),
  },
});

export type UploadResponse = {
  uploadURL?: string;
  body?: { location?: string; key?: string; bucket?: string };
};

type BucketAndKey = {
  Bucket: string;
  Key: string;
};

const parsePathStyleUrl = (pathParts: string[]): BucketAndKey | null => {
  if (pathParts.length < 1) {
    return null;
  }
  const Bucket = pathParts[0];
  const Key = pathParts.slice(1).join('/');
  return Bucket ? { Bucket, Key } : null;
};

// https://unifyappsstorageext.blob.core.windows.net/unifyapps-application-uploads/user-uploads/1/_m5xehhjc-Screenshot_2025-01-09_at_9.15.14%E2%80%AFPM.png
const parseAzureBlobUrl = (urlObj: URL, pathParts: string[]): BucketAndKey | null => {
  if (!urlObj.hostname.includes('.blob.core.windows.net')) {
    return null;
  }
  return parsePathStyleUrl(pathParts);
};

// https://accelaero.compat.objectstorage.me-abudhabi-1.oraclecloud.com/unifyapps-isa-application-uploads/user-uploads/1/_m5xewp4p-10.4-MB.csv
const parseOracleCloudUrl = (urlObj: URL, pathParts: string[]): BucketAndKey | null => {
  if (!urlObj.hostname.includes('.oraclecloud.com')) {
    return null;
  }
  return parsePathStyleUrl(pathParts);
};

// https://s3.ap-south-1.amazonaws.com/qa.unifyapps.com.cloudstorage.1/user-uploads%2F1%2F_m5vc8sjv-Screenshot_2025-01-09_at_9.15.14%E2%80%AFPM.png
// https://qa.unifyapps.com.cloudstorage.1.s3.ap-south-1.amazonaws.com/user-uploads%2F1%2F_m5vc8sjv-Screenshot_2025-01-09_at_9.15.14%E2%80%AFPM.png
// https://qa.unifyapps.com.cloudstorage.1.s3.dualstack.ap-south-1.amazonaws.com/user-uploads/1/_m5vc8sjv-Screenshot_2025-01-09_at_9.15.14%E2%80%AFPM.png
const parseAwsS3VirtualHostedUrl = (hostname: string, pathParts: string[]): BucketAndKey | null => {
  // Regular virtual-hosted-style: bucket.s3.region.amazonaws.com
  // check for last occurrence of .s3. since bucket name can contain .s3.
  const s3Index = hostname.lastIndexOf('.s3.');
  if (s3Index > 0) {
    const Bucket = hostname.substring(0, s3Index);
    const Key = pathParts.join('/');
    return Bucket ? { Bucket, Key } : null;
  }

  return null;
};

const parseAwsS3Url = (urlObj: URL, pathParts: string[]): BucketAndKey | null => {
  if (!urlObj.hostname.includes('.amazonaws.com')) {
    return null;
  }

  // Try virtual-hosted-style first
  const virtualHostedResult = parseAwsS3VirtualHostedUrl(urlObj.hostname, pathParts);
  if (virtualHostedResult) {
    return virtualHostedResult;
  }

  // Fallback to path-style
  return parsePathStyleUrl(pathParts);
};

export const getBucketAndKey = (url?: string, resp?: UploadResponse['body']): BucketAndKey => {
  // prefer response body key and bucket if available
  if (resp?.key && resp?.bucket) {
    return { Bucket: resp.bucket, Key: resp.key };
  }

  if (typeof url !== 'string' || !url.trim()) {
    return { Bucket: '', Key: '' };
  }

  try {
    const urlObj = new URL(url);
    const pathname = decodeURIComponent(urlObj.pathname);
    const pathParts = pathname.split('/').filter(Boolean);

    const azureResult = parseAzureBlobUrl(urlObj, pathParts);
    if (azureResult) return azureResult;

    const oracleResult = parseOracleCloudUrl(urlObj, pathParts);
    if (oracleResult) return oracleResult;

    const awsResult = parseAwsS3Url(urlObj, pathParts);
    if (awsResult) return awsResult;
  } catch (error) {
    console.warn('Failed to parse URL for bucket and key extraction:', url, error);
  }

  return { Bucket: '', Key: '' };
};

/**
 * This function is fallback to get Private url, private url should be returned from BE after upload is complete there we have key and bucket
 * if on uppy server private url is failed then we are also retrying from here
 *
 * First we try using bucket and key from the response body if available, if not then we try using the upload URL to get the bucket and key
 * @param file - The file object
 * @param resp - The response object
 * @param queryMeta - The query meta object
 * @returns The private url
 */
async function getPrivateUrl(
  file: UploadedFile,
  resp?: UploadResponse,
  queryMeta?: QueryMeta,
): Promise<{ uploadURL?: string; body?: { location?: string } } | undefined> {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- old code need to check why it was added
  const uploadURL = resp?.uploadURL ?? (resp?.body?.location as string);
  if (isPrivateURL(uploadURL)) {
    return Promise.resolve(resp);
  }

  const { Key, Bucket } = getBucketAndKey(uploadURL, resp?.body);

  if (!Key || !Bucket) {
    return Promise.resolve(resp);
  }

  if (file.meta.publicAcl) {
    return { ...resp, uploadURL: `https://${Bucket}/${Key}` };
  }

  const usesAssetNode = Boolean(
    file.meta.accessScope && ASSET_NODE_SCOPES.has(file.meta.accessScope),
  );

  try {
    const response = await executeWorkflowNode(
      usesAssetNode ? getAssetNodeRequest(file, Key, Bucket) : getPathNodeRequest(Key, Bucket),
      { meta: usesAssetNode ? withAppIdHeader(queryMeta) : queryMeta },
    );

    // the path node returns `path`, the asset node `assetUrl`; neither is on the generated type
    const nodeResponse = response.response as { path?: string; assetUrl?: string } | undefined;
    const apiPath = nodeResponse?.path ?? nodeResponse?.assetUrl;
    const domain = getDomain(queryMeta || {});
    if (apiPath && domain) {
      // Building full url via custom function since in both the parameters `/` can come unnecessarily
      let uploadURLWithBase = buildUrl(domain, {
        path: apiPath,
      });

      if (queryMeta?.isPublicInterface) {
        uploadURLWithBase = publicApiURL(uploadURLWithBase, queryMeta);
      }

      if (queryMeta?.isPublicPage) {
        uploadURLWithBase = publicPageURL(uploadURLWithBase);
      }

      // remove the origin from the URL to make it relative
      const urlObj = new URL(uploadURLWithBase);
      const urlWithOutOrigin = uploadURLWithBase.replace(urlObj.origin, '');

      return {
        ...resp,
        uploadURL: urlWithOutOrigin,
      };
    }
    throw new Error('Error in private URL Resp');
  } catch (error) {
    console.error('Error getting private URL', error);
    throw error;
  }
}

export default getPrivateUrl;
